import axios from 'axios'
import { useUserStore } from '../stores/user'

// Create axios instance
const api = axios.create({
    baseURL: import.meta.env.VITE_API_URL || '/api',
    timeout: 30000, // Increased to 30 seconds
    headers: {
        'Content-Type': 'application/json',
    },
})

// Request interceptor
api.interceptors.request.use(
    (config) => {
        // Add auth token if available
        const token = localStorage.getItem('token')
        if (token) {
            config.headers.Authorization = `Bearer ${token}`
        }
        return config
    },
    (error) => {
        return Promise.reject(error)
    }
)

// Response interceptor
api.interceptors.response.use(
    (response) => {
        return response
    },
    (error) => {
        // Handle authentication errors
        if (error.response?.status === 401) {
            const errorData = error.response.data

            // Clear invalid token
            localStorage.removeItem('token')


            // Handle session-specific errors
            if (errorData?.error === 'Session invalidated' ||
                errorData?.error === 'Session expired' ||
                errorData?.error === 'Token expired') {

                // Force logout and redirect to login
                const userStore = useUserStore()
                userStore.logout()

                // Tell the user why after the reload; the landing page shows this once
                const notice = errorData.error === 'Session invalidated'
                    ? errorData.message
                    : 'Your session expired. Please log in again.'
                sessionStorage.setItem('authNotice', notice)

                // Redirect to login page
                if (window.location.pathname !== '/') {
                    window.location.href = '/'
                }
            }
        }

        // Handle network errors
        if (!error.response) {
            console.error('Network error:', error)
            return Promise.reject({
                message: 'Network error. Please check your connection.',
                isNetworkError: true
            })
        }

        return Promise.reject(error)
    }
)

// API methods
export const authAPI = {
    // Login
    login: (credentials) => api.post('/auth/login', credentials),

    // Logout (ends the session on the server)
    logout: () => api.post('/auth/logout'),

    // Get current user
    getCurrentUser: () => api.get('/auth/me'),

    // Change password
    changePassword: (currentPassword, newPassword) =>
        api.post('/auth/change-password', { currentPassword, newPassword })
}

export const productsAPI = {
    // Get all products
    getProducts: (params = {}) => api.get('/products', { params }),

    // Get product by SKU
    getProduct: (sku, isPublic = false) =>
        api.get(`/products/${sku}`, { params: { public: isPublic } }),

    // Get product by secret key
    getProductBySecretKey: (secretKey) =>
        api.get(`/products/secret/${secretKey}`),

    // Get product by secret key without authentication
    getProductBySecretKeyPublic: (secretKey) => {
        // Create a new axios instance without auth interceptor for this specific call
        const publicApi = axios.create({
            baseURL: import.meta.env.VITE_API_URL || '/api',
            timeout: 10000,
            headers: {
                'Content-Type': 'application/json',
            },
        })
        return publicApi.get(`/products/secret/${secretKey}`)
    },

    // Create product (admin only)
    createProduct: (productData) => api.post('/products', productData),

    // Update product (admin only)
    updateProduct: (id, productData) => api.patch(`/products/${id}`, productData),

    // Delete product (admin only)
    deleteProduct: (id) => api.delete(`/products/${id}`),

    // Get categories
    getCategories: () => api.get('/products/categories/list'),

    // Bulk import (admin only)
    bulkImport: (products) => api.post('/products/bulk-import', { products })
}

export const ordersAPI = {
    // Create order
    createOrder: (orderData) => api.post('/orders', orderData),

    // Get customer's orders
    getMyOrders: (params = {}) => api.get('/orders/my-orders', { params }),

    // Get orders (admin)
    getOrders: (params = {}) => api.get('/orders', { params }),

    // Get single order
    getOrder: (id) => api.get(`/orders/${id}`),

    // Get order details (for customers)
    getOrderDetails: (id) => api.get(`/orders/customer/${id}`),

    // Update order status
    updateOrderStatus: (id, status, notes) => api.patch(`/orders/${id}/status`, { status, notes }),

    // Assign order to workshop
    assignOrder: (id, workshopUserId, dueDate) => api.patch(`/orders/${id}/assign`, {
        workshop_user_id: workshopUserId,
        due_date: dueDate
    }),

    // Cancel order
    cancelOrder: (id) => api.patch(`/orders/${id}/cancel`),

    // Send notification
    sendNotification: (id, messageType) => api.post(`/orders/${id}/notify`, { message_type: messageType })
}

export const workshopAPI = {
    // Get workshop jobs
    getJobs: (params = {}) => api.get('/workshop/jobs', { params }),

    // Get single job
    getJob: (id) => api.get(`/workshop/jobs/${id}`),

    // Update job status
    updateJobStatus: (id, status, notes) =>
        api.patch(`/workshop/jobs/${id}/status`, { status, notes }),

    // Accept job
    acceptJob: (id) => api.patch(`/workshop/jobs/${id}/accept`),

    // Assign job (admin only)
    assignJob: (id, workshopUserId, dueDate) => api.patch(`/workshop/jobs/${id}/assign`, {
        workshop_user_id: workshopUserId,
        due_date: dueDate
    }),

    // Get workshop stats
    getStats: (params = {}) => api.get('/workshop/stats', { params }),


}

export const adminAPI = {
    // Get dashboard stats
    getDashboardStats: () => api.get('/admin/dashboard'),

    // Get analytics
    getAnalytics: (params = {}) => api.get('/admin/analytics', { params }),

    // Export analytics
    exportAnalytics: (params = {}) => api.get('/admin/analytics/export', { params }),

    // Get users
    getUsers: (params = {}) => api.get('/admin/users', { params }),

    // Get orders (admin)
    getOrders: (params = {}) => api.get('/admin/orders', { params }),

    // Get single user
    getUser: (id) => api.get(`/admin/users/${id}`),

    // Create user
    createUser: (userData) => api.post('/admin/users', userData),

    // Update user
    updateUser: (id, userData) => api.patch(`/admin/users/${id}`, userData),

    // Delete user
    deleteUser: (id) => api.delete(`/admin/users/${id}`),

    // Get products (admin)
    getProducts: (params = {}) => api.get('/admin/products', { params }),

    // Get single product (admin)
    getProduct: (id) => api.get(`/admin/products/${id}`),

    // Create product (admin)
    createProduct: (productData) => api.post('/admin/products', productData),

    // Update product (admin)
    updateProduct: (id, productData) => api.patch(`/admin/products/${id}`, productData),

    // Delete product (admin)
    deleteProduct: (id) => api.delete(`/admin/products/${id}`),

    // Upload images
    uploadImages: (files) => {
        const formData = new FormData()
        files.forEach((file, index) => {
            formData.append('images', file)
        })

        // Create a new axios instance for file upload
        const uploadApi = axios.create({
            baseURL: import.meta.env.VITE_API_URL || '/api',
            timeout: 30000, // Longer timeout for file uploads
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        })

        // Add auth token
        const token = localStorage.getItem('token')
        if (token) {
            uploadApi.defaults.headers.Authorization = `Bearer ${token}`
        }

        return uploadApi.post('/upload/images', formData)
    },

    // Delete image
    deleteImage: (filename) => api.delete(`/upload/images/${filename}`),

    // Get upload info
    getUploadInfo: () => api.get('/upload/info'),


    // Generate invite token
    generateInviteToken: (inviteData) => api.post('/admin/invites', inviteData),

    // Get all invites
    getInvites: () => api.get('/admin/invites'),

    // Revoke invite token
    revokeInviteToken: (inviteId) => api.patch(`/admin/invites/${inviteId}/revoke`),

    // Assign labels to product
    assignLabels: (productId, labelIds) => api.post('/labels/assign', { product_id: productId, label_ids: labelIds })
}

export const labelsAPI = {
    // Get all labels
    getLabels: (type) => api.get('/labels', { params: type ? { type } : {} }),
    // Get labels grouped by type
    getLabelsGrouped: () => api.get('/labels/grouped'),
    // Create label
    createLabel: (data) => api.post('/labels', data),
    // Update label
    updateLabel: (id, data) => api.put(`/labels/${id}`, data),
    // Delete label
    deleteLabel: (id) => api.delete(`/labels/${id}`),
    // Assign labels to product
    assignLabels: (productId, labelIds) => api.post('/labels/assign', { product_id: productId, label_ids: labelIds }),
    // Get labels for a product
    getProductLabels: (productId) => api.get(`/labels/product/${productId}`),
    // Remove label from product
    removeProductLabel: (productId, labelId) => api.delete(`/labels/product/${productId}/${labelId}`)
};

// Filtering products by labels
export const filterAPI = {
    filterProducts: (labelIds, page = 1, limit = 20) => api.post('/products/filter', { label_ids: labelIds, page, limit })
};

// Cart management (local storage)
export const cartAPI = {
    // Get user's cart
    getCart: async () => {
        try {
            const response = await api.get('/cart')
            return response.data.items || []
        } catch (error) {
            console.error('Failed to get cart:', error)
            return []
        }
    },

    // Add item to cart
    addToCart: async (product, quantity = 1) => {
        try {
            const response = await api.post('/cart/items', {
                product_id: product.id,
                quantity
            })
            return response.data
        } catch (error) {
            console.error('Failed to add to cart:', error)
            throw error
        }
    },

    // Update cart item quantity
    updateCartItem: async (cartItemId, quantity) => {
        try {
            const response = await api.patch(`/cart/items/${cartItemId}`, {
                quantity
            })
            return response.data
        } catch (error) {
            console.error('Failed to update cart item:', error)
            throw error
        }
    },

    // Remove item from cart
    removeFromCart: async (cartItemId) => {
        try {
            const response = await api.delete(`/cart/items/${cartItemId}`)
            return response.data
        } catch (error) {
            console.error('Failed to remove from cart:', error)
            throw error
        }
    },

    // Clear user's cart
    clearCart: async () => {
        try {
            const response = await api.delete('/cart')
            return response.data
        } catch (error) {
            console.error('Failed to clear cart:', error)
            throw error
        }
    },

    // Get cart summary
    getCartSummary: async () => {
        try {
            const response = await api.get('/cart')
            return response.data.summary || {
                total_items: 0,
                total_weight: 0,
                total_amount: 0
            }
        } catch (error) {
            console.error('Failed to get cart summary:', error)
            return {
                total_items: 0,
                total_weight: 0,
                total_amount: 0
            }
        }
    }
}

// User Roles API
export const userRolesAPI = {
    // Get all user roles
    getRoles: async () => {
        try {
            const response = await api.get('/user-roles')
            return response.data
        } catch (error) {
            console.error('Failed to fetch user roles:', error)
            throw error
        }
    },

    // Get user role by ID
    getRole: async (id) => {
        try {
            const response = await api.get(`/user-roles/${id}`)
            return response.data
        } catch (error) {
            console.error('Failed to fetch user role:', error)
            throw error
        }
    },

    // Create new user role
    createRole: async (roleData) => {
        try {
            const response = await api.post('/user-roles', roleData)
            return response.data
        } catch (error) {
            console.error('Failed to create user role:', error)
            throw error
        }
    },

    // Update user role
    updateRole: async (id, roleData) => {
        try {
            const response = await api.put(`/user-roles/${id}`, roleData)
            return response.data
        } catch (error) {
            console.error('Failed to update user role:', error)
            throw error
        }
    },

    // Delete user role
    deleteRole: async (id) => {
        try {
            const response = await api.delete(`/user-roles/${id}`)
            return response.data
        } catch (error) {
            console.error('Failed to delete user role:', error)
            throw error
        }
    }
}

// Order Statuses API
export const orderStatusesAPI = {
    // Get all order statuses
    getStatuses: async () => {
        try {
            const response = await api.get('/order-statuses')
            return response.data
        } catch (error) {
            console.error('Failed to fetch order statuses:', error)
            throw error
        }
    },

    // Get order status by ID
    getStatus: async (id) => {
        try {
            const response = await api.get(`/order-statuses/${id}`)
            return response.data
        } catch (error) {
            console.error('Failed to fetch order status:', error)
            throw error
        }
    },

    // Create new order status
    createStatus: async (statusData) => {
        try {
            const response = await api.post('/order-statuses', statusData)
            return response.data
        } catch (error) {
            console.error('Failed to create order status:', error)
            throw error
        }
    },

    // Update order status
    updateStatus: async (id, statusData) => {
        try {
            const response = await api.put(`/order-statuses/${id}`, statusData)
            return response.data
        } catch (error) {
            console.error('Failed to update order status:', error)
            throw error
        }
    },

    // Delete order status
    deleteStatus: async (id) => {
        try {
            const response = await api.delete(`/order-statuses/${id}`)
            return response.data
        } catch (error) {
            console.error('Failed to delete order status:', error)
            throw error
        }
    }
}

// Workshop Statuses API
export const workshopStatusesAPI = {
    // Get all workshop statuses
    getStatuses: async () => {
        try {
            const response = await api.get('/workshop-statuses')
            return response.data
        } catch (error) {
            console.error('Failed to fetch workshop statuses:', error)
            throw error
        }
    },

    // Get workshop status by ID
    getStatus: async (id) => {
        try {
            const response = await api.get(`/workshop-statuses/${id}`)
            return response.data
        } catch (error) {
            console.error('Failed to fetch workshop status:', error)
            throw error
        }
    },

    // Create new workshop status
    createStatus: async (statusData) => {
        try {
            const response = await api.post('/workshop-statuses', statusData)
            return response.data
        } catch (error) {
            console.error('Failed to create workshop status:', error)
            throw error
        }
    },

    // Update workshop status
    updateStatus: async (id, statusData) => {
        try {
            const response = await api.put(`/workshop-statuses/${id}`, statusData)
            return response.data
        } catch (error) {
            console.error('Failed to update workshop status:', error)
            throw error
        }
    },

    // Delete workshop status
    deleteStatus: async (id) => {
        try {
            const response = await api.delete(`/workshop-statuses/${id}`)
            return response.data
        } catch (error) {
            console.error('Failed to delete workshop status:', error)
            throw error
        }
    }
}

export default api 